import React, { useState, useContext, Dispatch, SetStateAction, useEffect } from 'react';
import { Grid, makeStyles, createStyles, Theme, useMediaQuery, Collapse, Button, TextField } from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import SelectCustomerType from './Filters/SelectCustomerType';
import { Premise, EntityResponse, Expert, PremiseLocation, Speciality, Campaign, SelectedFilters, CampaignService, Language, UISettings, InputName } from '../types';
import SearchPremise from "./Filters/SearchPremise";
import SearchService from "./Filters/SearchService";
import SearchExpert from "./Filters/SearchExpert";
import ExtraFilters from './Filters/ExtraFilters/ExtraFilters';
import { theme, colors } from '../theme';
import { LocalizationContext } from './LanguageContext/LocalizationContext';
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreRounded';
import ExpandLessIcon from '@material-ui/icons/ExpandLessRounded';
import CalendarFilter from './CalendarFilter';
import DateFnsUtils from '@date-io/date-fns';
import { LocalizedUtils } from './LocalizedUtils';
import moment from 'moment';
import CalendarTodayIcon from '@material-ui/icons/CalendarTodayRounded';
import FullWidthDialog from './FullWidthDialog';
import {getDisabledFields} from './FilterShared';
import BookingInfoDialogButton from './Filters/BookingInfoDialogButton';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: "#f7f3ee",
            textTransform: "none",
        },
        itemMobile: {
            paddingBottom: theme.spacing(4),
            "&:last-child": {
                paddingBottom: 0
            }
        },
        itemDesktop: {
            paddingRight: theme.spacing(2),
            "&:last-child": {
                paddingBottom: 0,
                paddingRight: 0
            }
        },
        collapse: {
            width: "100%",
            paddingBottom: (mobile) => mobile ? theme.spacing(2) : 0,
        },
        button: {
            fontFamily: "Gotham Narrow Bold",
            fontSize: (props: { mobile: boolean }) => props.mobile ? '17px' : '18px',
            color: colors.darkBlue,
            paddingLeft: 0,
            textTransform: "none"
        },
        buttonGrid: {
            paddingTop: theme.spacing(4)
        },
        dateInput: {
            // Changes style of child elements except the last one.
            '& > *:not(:last-child)': {
                // Sets the space between label and input:
                marginTop: '-5px',
            },
            cursor: "none",
            backgroundColor: theme.palette.common.white,
            borderColor: "#979797",
            borderRadius: "4px",
            '& .MuiInputLabel-root': {
                fontSize: '21px',
                fontFamily: "Gotham Narrow Book"
              },
            "& label": {
                color: "#595959",
                fontFamily: "Gotham Narrow Medium",
                "&::placeholder": {
                    opacity: 0.7
                }
            },
            "& .MuiInputLabel-outlined": {
                transform: "translate(6px, -16px) scale(0.75)"
            },
            "& legend": {
                maxWidth: 0
            },
            '& label.Mui-focused': {
                color: theme.palette.primary.main,
            },
            '& .MuiOutlinedInput-root': {
                color: "#595959",
                '& fieldset': {
                    borderWidth: "1px",
                    borderColor: "#979797",
                },
                '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                    borderWidth: "1px",
                    borderColor: theme.palette.primary.main,
                },
            },
            "& .MuiOutlinedInput-input": {
                cursor: "none",
                color: "#595959"
            }
        }
    })
);

interface FilterMenuProps {
    date: MaterialUiPickersDate,
    onDateChange: (date: MaterialUiPickersDate) => void,
    premises: EntityResponse<Premise>[],
    selectedServices: CampaignService[],
    setSelectedFilters: Dispatch<SetStateAction<SelectedFilters>>,
    position?: GeolocationPosition | null,
    selectedExperts: EntityResponse<Expert>[],
    timeselectListFilters: string[] | null,
    locationFilters: string[],
    expertFilters: string[],
    services: CampaignService[],
    locationUuids: PremiseLocation[] | null,
    languageFilters: string[],
    specialityFilters: string[],
    selectedSpecialities: EntityResponse<Speciality>[],
    selectedLanguages: EntityResponse<Language>[],
    serviceFilters: string[],
    localization: Locale,
    utils: typeof DateFnsUtils | typeof LocalizedUtils,
    onCalendarModalOpen: () => void,
    onCalendarModalClose: () => void,
    calendarModalOpen: boolean,
    slots: string[],
    setSlots: (slots: string[]) => void,
    handleMonthChange: (renderedDay: MaterialUiPickersDate) => Promise<void>,
    enableLocation: () => void,
    disableLocation: () => void,
    customerType: string,
    campaign?: Campaign,
    loadingInitialState: boolean,
    availableCustomerTypes: string[],
    setPopulatingFilters: Dispatch<SetStateAction<boolean>>,
    accessToken: string | null,
    setAccessToken: Dispatch<SetStateAction<string | null>>,
    uiSettings: UISettings | undefined,
    showCalendar: boolean
}
interface FilterMenuState{
    /* This value indicates if the extra filters should be shown. */
    checked: boolean,
    /* This value is used to lock the expand state once the user has made changes to it. */
    extraFilterStateChangedByUser: boolean,
    /* This value is used to show an empty grid instead of extra filters before settings are loaded. */
    uiSettingsLoaded: boolean,
    /* Input elements in order that needs to be set before other elements become enabled */
    requiredInOrder: InputName[],
    /* Changes the input order in the FilterMenu. */
    orderOfInputs: InputName[],
    /* Only customer type selection is shown first. */
    showOnlyCustomerTypeSelectionFirst: boolean
}


export const FilterMenu: React.FC<FilterMenuProps> = ({
    premises,
    selectedExperts,
    selectedServices,
    setSelectedFilters,
    locationFilters,
    expertFilters,
    services,
    locationUuids,
    position,
    languageFilters,
    specialityFilters,
    serviceFilters,
    timeselectListFilters,
    date,
    onDateChange,
    utils,
    localization,
    onCalendarModalClose,
    onCalendarModalOpen,
    calendarModalOpen,
    slots,
    setSlots,
    handleMonthChange,
    enableLocation,
    customerType,
    campaign,
    loadingInitialState,
    selectedSpecialities,
    selectedLanguages,
    availableCustomerTypes,
    setPopulatingFilters,
    accessToken,
    setAccessToken,
    uiSettings,
    showCalendar,
}) => {
    const mobile = useMediaQuery(theme.breakpoints.down("md"));
    const classes = useStyles({ mobile });

    const [
        {checked,
        extraFilterStateChangedByUser,
        showOnlyCustomerTypeSelectionFirst,
        uiSettingsLoaded,
        requiredInOrder,
        orderOfInputs},
        setFilterMenuState
    ] = useState<FilterMenuState>({
            checked: false,
            extraFilterStateChangedByUser: false,
            uiSettingsLoaded: false,
            requiredInOrder: [],
            orderOfInputs: [],
            showOnlyCustomerTypeSelectionFirst: false,
    });
    const { translation, getLanguage } = useContext(LocalizationContext);
    const language = getLanguage();

    const openDetailedSearch = (open: boolean) => (_e: any) => {
        setFilterMenuState(prevState => ({
            ...prevState,
            extraFilterStateChangedByUser: true,
            checked: open
        }));
    };

    useEffect(() => {
        if(extraFilterStateChangedByUser) {
            return;
        }
        const uiSettingsLoaded = !!uiSettings;
        const expandDesktopExtraFilters = !!uiSettings?.expandDesktopExtraFilters;
        const requiredInOrder = uiSettings?.requiredInOrder || [];
        const orderOfInputs = uiSettings?.orderOfInputs || [];
        const showOnlyCustomerTypeSelectionFirst =
            uiSettings?.showOnlyCustomerTypeSelectionFirst || false;
        const userSelectedValues =
            (selectedLanguages.length > 0 || selectedSpecialities.length > 0);
        const checked = userSelectedValues || (!mobile && expandDesktopExtraFilters);

        setFilterMenuState(prevState => ({
            ...prevState,
            checked,
            uiSettingsLoaded,
            extraFilterStateChangedByUser: userSelectedValues,
            requiredInOrder,
            orderOfInputs,
            showOnlyCustomerTypeSelectionFirst
        }));

    }, [
        mobile,
        uiSettings,
        extraFilterStateChangedByUser,
        selectedLanguages,
        selectedSpecialities]);
    const disabledFields = getDisabledFields(customerType, selectedServices, requiredInOrder);
    const showOnlyCustomerType = showOnlyCustomerTypeSelectionFirst && customerType.length === 0;
    const getExtraFilters = (uiSettingsLoaded: boolean, checked: boolean, disabled: boolean) => {
        if(!uiSettingsLoaded) {
            return <Grid item xs={12} />;
        }
        const endIcon = !checked ? <ExpandMoreIcon /> : <ExpandLessIcon />;
        return <>
            <Grid item xs={12} className={classes.buttonGrid}>
                <Button
                    id={"showExtraFilters"}
                    variant="text"
                    onClick={openDetailedSearch(!checked)}
                    endIcon={endIcon}
                    className={classes.button}
                >
                    {!checked ? translation.showExtraFilters : translation.hideExtraFilters}
                </Button>
            </Grid>
            <Collapse
                className={classes.collapse}
                in={checked}
                timeout="auto"
            >
                <ExtraFilters
                    disabled={disabled}
                    languageFilters={languageFilters}
                    specialityFilters={specialityFilters}
                    selectedSpecialities={selectedSpecialities}
                    selectedLanguages={selectedLanguages}
                    setSelectedFilters={setSelectedFilters}
                    loadingInitialState={loadingInitialState}
                />
            </Collapse>
        </>;
    };

    const getInput = (order:number) => {
        const orderOfInputsValid = orderOfInputs?.length > 0  ? orderOfInputs : ["searchPremise","searchService"];
        const item = orderOfInputsValid[order];

        if(item === "searchPremise") {
            return <SearchPremise
            disabled={disabledFields.searchPremise}
            locationFilters={locationFilters}
            premises={premises}
            position={position}
            setSelectedFilters={setSelectedFilters}
            locationUuids={locationUuids}
            enableLocation={enableLocation}
            loadingInitialState={loadingInitialState}
            accessToken={accessToken}
            setAccessToken={setAccessToken} />;
        }
        if(item === "searchService") {
            return <SearchService
            disabled={disabledFields.searchService}
            services={services}
            selectedExperts={selectedExperts}
            selectedPremises={premises}
            setSelectedFilters={setSelectedFilters}
            selectedServices={selectedServices}
            serviceFilters={serviceFilters}
        />;
        }
    };

    // Show booking info if translation is provided
    const showBookingInfo1 = (language === "finnish" && uiSettings?.showBookingInfo1) || false;
    const showBookingInfo2 = (language === "finnish" && uiSettings?.showBookingInfo2) || false;
    const showRemoteBookingInfo = uiSettings?.showRemoteBookingInfo || false;

    return (
        <>

            <Grid
                container
                item
                spacing={0}
                className={classes.root}
                role={"search"}
                aria-label={translation.ariaLabelFilterMenu}
            >
                <Grid
                    item
                    md={12}
                    xs={12}
                    className={classes.root}
                >
                    {showBookingInfo1 &&
                        <BookingInfoDialogButton
                            noteText={ translation.limitedInternetReservationNote }
                            titleText={ translation.limitedInternetBookingTitle + ':' }
                            detailsText={ translation.limitedInternetBookingDetails }
                            translation={ translation }
                        />
                    }
                    {showBookingInfo2 &&
                        <BookingInfoDialogButton
                            noteText={ translation.limitedInternetReservationNote2 }
                            titleText={ translation.limitedInternetBookingTitle2 + ':' }
                            detailsText={ translation.limitedInternetBookingDetails2 }
                            translation={ translation }
                        />
                    }
                    {showRemoteBookingInfo &&
                        <BookingInfoDialogButton
                            noteText={ translation.remoteBookingInfoNote }
                            titleText={ translation.remoteBookingInfoTitle + ':' }
                            detailsText={ translation.remoteBookingInfoDetails }
                            translation={ translation }
                        />
                    }
                </Grid>

                <Grid item xs={12}>
                    <SelectCustomerType
                        customerType={customerType}
                        setSelectedFilters={setSelectedFilters}
                        availableCustomerTypes={availableCustomerTypes}
                        campaign={campaign}
                        setPopulatingFilters={setPopulatingFilters}
                    />
                </Grid>
                {!showOnlyCustomerType && <>
                    <Grid container item xs={12}>
                        <Grid
                            item
                            md={4}
                            xs={12}
                            className={mobile ? classes.itemMobile : classes.itemDesktop}
                        >
                            {getInput(0)}
                        </Grid>
                        <Grid
                            item
                            md={4}
                            xs={12}
                            className={mobile ? classes.itemMobile : classes.itemDesktop}
                        >
                            {getInput(1)}
                        </Grid>
                        <Grid
                            item
                            md={4}
                            xs={12}
                            className={mobile ? classes.itemMobile : classes.itemDesktop}
                        >
                            <SearchExpert
                                disabled={disabledFields.searchExpert}
                                expertFilters={expertFilters}
                                setSelectedFilters={setSelectedFilters}
                                loadingInitialState={loadingInitialState}
                                selectedExperts={selectedExperts}
                                services={services}
                            />
                        </Grid>
                        {mobile && showCalendar &&
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.dateInput}
                                    placeholder={translation.selectDay}
                                    fullWidth
                                    variant="outlined"
                                    label={translation.day}
                                    value={!date ?
                                        null :
                                        date}
                                    onClick={onCalendarModalOpen}
                                    InputProps={{
                                        endAdornment: (<CalendarTodayIcon />),
                                        readOnly: true,
                                        value: date ? moment(date).format("YYYY-MM-DD") : null
                                    }}
                                />

                                <FullWidthDialog
                                    header={translation.selectDay}
                                    open={calendarModalOpen}
                                    handleClose={onCalendarModalClose}
                                >
                                    <CalendarFilter
                                        date={date}
                                        filters={timeselectListFilters}
                                        onDateChange={onDateChange}
                                        utils={utils}
                                        localization={localization}
                                        slots={slots}
                                        setSlots={setSlots}
                                        handleMonthChange={handleMonthChange}
                                    />
                                </FullWidthDialog>
                            </Grid>
                        }
                    </Grid>
                    {getExtraFilters(uiSettingsLoaded, checked, disabledFields.extraFilters)}
                </>}
            </Grid>
        </>
    );
};
